import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import "../styles/ResetPassword.css";

function LockIcon() {
  return (
    <svg
      width="28"
      height="28"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      <path d="M12 15v3" />
    </svg>
  );
}

function ShieldIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    </svg>
  );
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  const handleReset = async (event) => {
    event.preventDefault();
    setError("");

    if (!token) {
      setError("Invalid reset link. Please request a new one.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!API_URL) {
      setError("The service is unavailable. Please try again later.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : "Unable to reset your password. Your link may have expired."
        );
      }

      setSuccess(true);
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-layout">
      {/* Left branding panel */}
      <aside className="rp-brand-panel">
        <Link to="/" className="rp-logo">
          <span className="rp-logo-mark" aria-hidden="true">
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m6 7 5 5-5 5M13 7l5 5-5 5" />
            </svg>
          </span>
          <span>ConvertPro</span>
        </Link>

        <div className="rp-brand-content">
          <p className="rp-eyebrow">SECURE ACCOUNT RECOVERY</p>

          <h1>
            A fresh password.
            <br />
            A secure start.
          </h1>

          <p className="rp-brand-description">
            Restore access to your account and get back to fast, reliable
            file conversions.
          </p>

          <div className="rp-security-note">
            <span className="rp-shield">
              <ShieldIcon />
            </span>
            <p>Choose a unique password you haven’t used elsewhere.</p>
          </div>
        </div>

        <p className="rp-copyright">
          © 2026 ConvertPro. All rights reserved.
        </p>
      </aside>

      {/* Right form panel */}
      <main className="rp-form-panel">
        <section className="rp-card" aria-labelledby="rp-title">
          <Link to="/" className="rp-back-home">
            <span aria-hidden="true">←</span>
            Back to Home
          </Link>

          <div className="rp-icon-badge">
            <LockIcon />
          </div>

          {success ? (
            <div className="rp-success">
              <h2 id="rp-title">Password reset!</h2>

              <p role="status">
                Your password has been updated successfully. You can now
                sign in with your new password.
              </p>

              <Link to="/login" className="rp-primary-btn">
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <header className="rp-header">
                <h2 id="rp-title">Reset your password</h2>
                <p>
                  Enter a new password to secure your ConvertPro account.
                </p>
              </header>

              {!token && (
                <p className="rp-error" role="alert">
                  This reset link is invalid. Please request a new one.
                </p>
              )}

              <form
                className="rp-form"
                onSubmit={handleReset}
                aria-busy={loading}
              >
                <div className="rp-field">
                  <label htmlFor="rp-password">New Password</label>

                  <div className="rp-input-wrap">
                    <input
                      id="rp-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your new password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      aria-describedby="rp-password-hint"
                      minLength={8}
                      required
                      disabled={loading || !token}
                    />

                    <button
                      type="button"
                      className="rp-show-btn"
                      onClick={() => setShowPassword((value) => !value)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      aria-pressed={showPassword}
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>

                  <p className="rp-hint" id="rp-password-hint">
                    Use at least 8 characters.
                  </p>
                </div>

                <div className="rp-field">
                  <label htmlFor="rp-confirm">Confirm New Password</label>

                  <div className="rp-input-wrap">
                    <input
                      id="rp-confirm"
                      type={showConfirm ? "text" : "password"}
                      placeholder="Repeat your new password"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(event) =>
                        setConfirmPassword(event.target.value)
                      }
                      minLength={8}
                      required
                      disabled={loading || !token}
                    />

                    <button
                      type="button"
                      className="rp-show-btn"
                      onClick={() => setShowConfirm((value) => !value)}
                      aria-label={
                        showConfirm
                          ? "Hide confirmed password"
                          : "Show confirmed password"
                      }
                      aria-pressed={showConfirm}
                    >
                      {showConfirm ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                {error && (
                  <p className="rp-error" role="alert">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  className="rp-primary-btn"
                  disabled={loading || !token}
                >
                  {loading ? "Resetting Password..." : "Reset Password"}
                </button>
              </form>

              <p className="rp-footer-link">
                Need a new reset link?{" "}
                <Link to="/forgot-password">Request one</Link>
              </p>

              <hr className="rp-divider" />

              <p className="rp-footer-link">
                Remember your password? <Link to="/login">Sign in</Link>
              </p>
            </>
          )}
        </section>
      </main>
    </div>
  );
}
